import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

/**
 * InvitationSignup Component - 2025 Design
 *
 * Uses same Auth.css branding as Login/Signup
 * Clean, minimal design following Apple/Google standards
 */

// SVG Icons
const Icons = {
  Eye: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  EyeOff: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/>
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/>
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/>
      <line x1="2" x2="22" y1="2" y2="22"/>
    </svg>
  ),
};

const InvitationSignup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    email: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Accept both 'invitation' and 'invite' query params for flexibility
  const invitationCode = searchParams.get('invitation') || searchParams.get('invite');

  useEffect(() => {
    const validateInvitation = async () => {
      try {
        setLoading(true);
        const response = await axios.post('/api/group/validate-invitation', {
          invitation_code: invitationCode
        });

        if (response.data.success) {
          setInvitationData(response.data.invitation);
          // Pre-fill email if provided in invitation
          if (response.data.invitation.email) {
            setFormData(prev => ({
              ...prev,
              email: response.data.invitation.email
            }));
          }
        } else {
          setError(response.data.error || 'Invalid invitation code');
        }
      } catch (err) {
        console.error('Error validating invitation:', err);
        setError('Failed to validate invitation. Please check the link and try again.');
      } finally {
        setLoading(false);
      }
    };

    if (invitationCode) {
      validateInvitation();
    } else {
      setError('No invitation code provided');
      setLoading(false);
    }
  }, [invitationCode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      setFormErrors({});

      const response = await axios.post('/api/group/accept-invitation', {
        invitation_code: invitationCode,
        username: formData.username,
        password: formData.password,
        name: formData.name,
        email: formData.email
      });

      if (response.data.success) {
        // HttpOnly cookies are set by the backend automatically
        // Redirect straight to tutor - no onboarding needed for invited students
        window.location.href = '/tutor';
      } else {
        setError(response.data.error || 'Failed to create account');
      }
    } catch (err) {
      console.error('Error creating account:', err);
      setError('Failed to create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <img src="/images/TAPPY-logo-black.png" alt="Tappy" />
          </div>
          <div className="auth-header">
            <h1 className="auth-title">Validating Invitation</h1>
            <p className="auth-subtitle">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <img src="/images/TAPPY-logo-black.png" alt="Tappy" />
          </div>
          <div className="auth-header">
            <h1 className="auth-title">Invalid Invitation</h1>
            <p className="auth-subtitle">{error}</p>
          </div>
          <div className="auth-error">
            This invitation link may be expired, already used, or invalid.
          </div>
          <button
            className="auth-btn-primary"
            onClick={() => navigate('/')}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Main signup form
  return (
    <div className="auth-page">
      <div className="auth-card">
        {/* Logo */}
        <div className="auth-logo">
          <img src="/images/TAPPY-logo-black.png" alt="Tappy" />
        </div>

        {/* Header */}
        <div className="auth-header">
          <h1 className="auth-title">Join {invitationData.account_manager_name}'s Group</h1>
          <p className="auth-subtitle">Create your account to start learning with Tappy</p>
        </div>

        {/* Invitation Info */}
        <div className="auth-info">
          <strong>Invited by:</strong> {invitationData.account_manager_name}
        </div>

        {/* Error display */}
        {Object.keys(formErrors).length > 0 && (
          <div className="auth-error">
            Please fix the errors below to continue.
          </div>
        )}

        {/* Signup Form */}
        <form onSubmit={handleSubmit} className="auth-form">
          {/* Username */}
          <div className="auth-input-group">
            <label className="auth-label">Username</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              className={`auth-input ${formErrors.username ? 'error' : ''}`}
              placeholder="Choose a username"
              disabled={submitting}
              autoComplete="username"
            />
            {formErrors.username && (
              <span style={{ color: 'var(--color-error)', fontSize: '13px' }}>{formErrors.username}</span>
            )}
          </div>

          {/* Password */}
          <div className="auth-input-group">
            <label className="auth-label">Password</label>
            <div className="auth-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`auth-input auth-input-with-toggle ${formErrors.password ? 'error' : ''}`}
                placeholder="Create a password"
                disabled={submitting}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
              </button>
            </div>
            {formErrors.password && (
              <span style={{ color: 'var(--color-error)', fontSize: '13px' }}>{formErrors.password}</span>
            )}
          </div>

          {/* Confirm Password */}
          <div className="auth-input-group">
            <label className="auth-label">Confirm Password</label>
            <div className="auth-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`auth-input auth-input-with-toggle ${formErrors.confirmPassword ? 'error' : ''}`}
                placeholder="Confirm your password"
                disabled={submitting}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
              >
                {showConfirmPassword ? <Icons.EyeOff /> : <Icons.Eye />}
              </button>
            </div>
            {formErrors.confirmPassword && (
              <span style={{ color: 'var(--color-error)', fontSize: '13px' }}>{formErrors.confirmPassword}</span>
            )}
          </div>

          {/* Full Name */}
          <div className="auth-input-group">
            <label className="auth-label">Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`auth-input ${formErrors.name ? 'error' : ''}`}
              placeholder="Enter your full name"
              disabled={submitting}
              autoComplete="name"
            />
            {formErrors.name && (
              <span style={{ color: 'var(--color-error)', fontSize: '13px' }}>{formErrors.name}</span>
            )}
          </div>

          {/* Email */}
          <div className="auth-input-group">
            <label className="auth-label">Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`auth-input ${formErrors.email ? 'error' : ''}`}
              placeholder="Enter your email"
              disabled={submitting}
              autoComplete="email"
            />
            {formErrors.email && (
              <span style={{ color: 'var(--color-error)', fontSize: '13px' }}>{formErrors.email}</span>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="auth-btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Creating Account...' : 'Join Group'}
          </button>
        </form>

        {/* Legal */}
        <div className="auth-legal">
          By creating an account, you agree to Tappy's{' '}
          <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
};

export default InvitationSignup;
